import gql from 'graphql-tag';
import * as Apollo from '@apollo/client';
import * as Types from '../../../../server/graphql/__generated.types';

import {
  ProjectFragmentDoc,
  ProjectAccessFragmentDoc,
  SubscriptionProjectAccessFragmentDoc,
} from '../../fragments.generated';

const defaultOptions = {} as const;
export type UserProjectAccessQueryVariables = Types.Exact<{
  inputs: Types.UserProjectInputs;
}>;

export type UserProjectAccessQuery = {
  __typename?: 'Query';
  user: {
    __typename?: 'User';
    id: string;
    project?: {
      __typename?: 'Project';
      id: string;
      createdAt: string;
      updatedAt: string;
      folderId?: string | null;
      driveId?: string | null;
      name: string;
      clientName?: string | null;
      clipsCount: number;
      creatorId: string;
      sequence: any;
      shareableUrl?: string | null;
      studioVersion: Types.StudioProjectVersion;
      sampleRate?: number | null;
      rootDriveId?: string | null;
      defaultPauseLength?: number | null;
      bulkPauseLength?: number | null;
      shareable?: {
        __typename?: 'ProjectShareable';
        url?: string | null;
        deletedAt?: string | null;
      } | null;
      projectAccess: {
        __typename?: 'ProjectAccess';
        id: string;
        viewerAccessRole?: {
          __typename?: 'ProjectAccessRole';
          id: Types.ProjectAccessRoleEnum;
          permissions: string;
        } | null;
        teamWideAccessRole?: {
          __typename?: 'ProjectAccessRole';
          id: Types.ProjectAccessRoleEnum;
          label: string;
          permissions: string;
        } | null;
        teamMemberAccess?: Array<{
          __typename?: 'TeamMemberAccess';
          id: string;
          teamMember: {
            __typename?: 'TeamMember';
            id: string;
            name: string;
            initials: string;
            avatar?: string | null;
          };
          projectAccessRole: {
            __typename?: 'ProjectAccessRole';
            id: Types.ProjectAccessRoleEnum;
            label: string;
            permissions: string;
          };
        }> | null;
      };
    } | null;
  };
};

export type EnableProjectSharingMutationVariables = Types.Exact<{
  input: Types.ProjectSharingEnableInputs;
}>;

export type EnableProjectSharingMutation = {
  __typename?: 'Mutation';
  enableProjectSharing: {
    __typename?: 'ProjectSharingEnableMutationResponse';
    success: boolean;
    code: string;
    message: string;
    project?: {
      __typename?: 'Project';
      id: string;
      shareable?: {
        __typename?: 'ProjectShareable';
        url?: string | null;
        deletedAt?: string | null;
      } | null;
    } | null;
  };
};

export type DisableProjectSharingMutationVariables = Types.Exact<{
  input: Types.ProjectSharingDisableInputs;
}>;

export type DisableProjectSharingMutation = {
  __typename?: 'Mutation';
  disableProjectSharing: {
    __typename?: 'ProjectSharingDisableMutationResponse';
    success: boolean;
    code: string;
    message: string;
    project?: {
      __typename?: 'Project';
      id: string;
      shareable?: {
        __typename?: 'ProjectShareable';
        url?: string | null;
        deletedAt?: string | null;
      } | null;
    } | null;
  };
};

export type UpdateProjectAccessTeamWideAccessRoleMutationVariables = Types.Exact<{
  inputs: Types.UpdateTeamWideAccessInputs;
}>;

export type UpdateProjectAccessTeamWideAccessRoleMutation = {
  __typename?: 'Mutation';
  updateProjectAccessTeamWideAccessRole: {
    __typename?: 'UpdateProjectAccessTeamWideAccessResponse';
    success: boolean;
    message: string;
    code: string;
    role?: {
      __typename?: 'ProjectAccessRole';
      id: Types.ProjectAccessRoleEnum;
      label: string;
      permissions: string;
    } | null;
    teamMemberAccess?: Array<{
      __typename?: 'TeamMemberAccess';
      id: string;
      teamId: string;
      projectId: string;
      teamMember: { __typename?: 'TeamMember'; id: string; name: string };
      projectAccessRole: {
        __typename?: 'ProjectAccessRole';
        id: Types.ProjectAccessRoleEnum;
        label: string;
        permissions: string;
      };
    }> | null;
  };
};

export type UpdateProjectAccessTeamMemberAccessRoleMutationVariables = Types.Exact<{
  inputs: Types.UpdateTeamMemberAccessInputs;
}>;

export type UpdateProjectAccessTeamMemberAccessRoleMutation = {
  __typename?: 'Mutation';
  updateProjectAccessTeamMemberAccessRole: {
    __typename?: 'UpdateProjectAccessTeamMemberAccessResponse';
    success: boolean;
    message: string;
    code: string;
    teamMemberId?: string | null;
    role?: {
      __typename?: 'ProjectAccessRole';
      id: Types.ProjectAccessRoleEnum;
      label: string;
      permissions: string;
    } | null;
  };
};

export type UpdateBulkTeamMemberAccessRoleMutationVariables = Types.Exact<{
  inputs: Types.UpdateBulkTeamMemberAccessInputs;
}>;

export type UpdateBulkTeamMemberAccessRoleMutation = {
  __typename?: 'Mutation';
  updateProjectAccessBulkTeamMemberAccessRole: {
    __typename?: 'UpdateProjectAccessBulkTeamMemberAccessResponse';
    success: boolean;
    message: string;
    code: string;
    teamMemberIds?: Array<string> | null;
    role?: {
      __typename?: 'ProjectAccessRole';
      id: Types.ProjectAccessRoleEnum;
      label: string;
      permissions: string;
    } | null;
  };
};

export type ProjectAccessEntityChangedSubscriptionVariables = Types.Exact<{
  inputs: Types.ProjectEntityChangedSubscriptionInputs;
}>;

export type ProjectAccessEntityChangedSubscription = {
  __typename?: 'Subscription';
  projectAccessEntityChanged: {
    __typename?: 'ProjectAccessEntityChangedEvent';
    projectId: string;
    changeType: Types.EntityChangeType;
    eventTime: string;
    projectAccessEntity: {
      __typename?: 'SubscriptionProjectAccess';
      id: string;
      projectId: string;
      userId?: string | null;
      teamId?: string | null;
      role?: Types.ProjectAccessRoleEnum | null;
      sequence: any;
    };
  };
};

export const UserProjectAccessDocument = gql`
  query UserProjectAccess($inputs: UserProjectInputs!) {
    user {
      id
      project(inputs: $inputs) {
        ...ProjectFragment
        ...ProjectAccessFragment
      }
    }
  }
  ${ProjectFragmentDoc}
  ${ProjectAccessFragmentDoc}
`;

/**
 * __useUserProjectAccessQuery__
 *
 * To run a query within a React component, call `useUserProjectAccessQuery` and pass it any options that fit your needs.
 * When your component renders, `useUserProjectAccessQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useUserProjectAccessQuery({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useUserProjectAccessQuery(
  baseOptions: Apollo.QueryHookOptions<
    UserProjectAccessQuery,
    UserProjectAccessQueryVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<UserProjectAccessQuery, UserProjectAccessQueryVariables>(
    UserProjectAccessDocument,
    options
  );
}
export function useUserProjectAccessLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<
    UserProjectAccessQuery,
    UserProjectAccessQueryVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<UserProjectAccessQuery, UserProjectAccessQueryVariables>(
    UserProjectAccessDocument,
    options
  );
}
export type UserProjectAccessQueryHookResult = ReturnType<
  typeof useUserProjectAccessQuery
>;
export type UserProjectAccessLazyQueryHookResult = ReturnType<
  typeof useUserProjectAccessLazyQuery
>;
export type UserProjectAccessQueryResult = Apollo.QueryResult<
  UserProjectAccessQuery,
  UserProjectAccessQueryVariables
>;
export const EnableProjectSharingDocument = gql`
  mutation enableProjectSharing($input: ProjectSharingEnableInputs!) {
    enableProjectSharing(inputs: $input) {
      success
      code
      message
      project {
        id
        shareable {
          url
          deletedAt
        }
      }
    }
  }
`;
export type EnableProjectSharingMutationFn = Apollo.MutationFunction<
  EnableProjectSharingMutation,
  EnableProjectSharingMutationVariables
>;

/**
 * __useEnableProjectSharingMutation__
 *
 * To run a mutation, you first call `useEnableProjectSharingMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useEnableProjectSharingMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [enableProjectSharingMutation, { data, loading, error }] = useEnableProjectSharingMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
export function useEnableProjectSharingMutation(
  baseOptions?: Apollo.MutationHookOptions<
    EnableProjectSharingMutation,
    EnableProjectSharingMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    EnableProjectSharingMutation,
    EnableProjectSharingMutationVariables
  >(EnableProjectSharingDocument, options);
}
export type EnableProjectSharingMutationHookResult = ReturnType<
  typeof useEnableProjectSharingMutation
>;
export type EnableProjectSharingMutationResult =
  Apollo.MutationResult<EnableProjectSharingMutation>;
export type EnableProjectSharingMutationOptions = Apollo.BaseMutationOptions<
  EnableProjectSharingMutation,
  EnableProjectSharingMutationVariables
>;
export const DisableProjectSharingDocument = gql`
  mutation disableProjectSharing($input: ProjectSharingDisableInputs!) {
    disableProjectSharing(inputs: $input) {
      success
      code
      message
      project {
        id
        shareable {
          url
          deletedAt
        }
      }
    }
  }
`;
export type DisableProjectSharingMutationFn = Apollo.MutationFunction<
  DisableProjectSharingMutation,
  DisableProjectSharingMutationVariables
>;

/**
 * __useDisableProjectSharingMutation__
 *
 * To run a mutation, you first call `useDisableProjectSharingMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDisableProjectSharingMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [disableProjectSharingMutation, { data, loading, error }] = useDisableProjectSharingMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
export function useDisableProjectSharingMutation(
  baseOptions?: Apollo.MutationHookOptions<
    DisableProjectSharingMutation,
    DisableProjectSharingMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    DisableProjectSharingMutation,
    DisableProjectSharingMutationVariables
  >(DisableProjectSharingDocument, options);
}
export type DisableProjectSharingMutationHookResult = ReturnType<
  typeof useDisableProjectSharingMutation
>;
export type DisableProjectSharingMutationResult =
  Apollo.MutationResult<DisableProjectSharingMutation>;
export type DisableProjectSharingMutationOptions = Apollo.BaseMutationOptions<
  DisableProjectSharingMutation,
  DisableProjectSharingMutationVariables
>;
export const UpdateProjectAccessTeamWideAccessRoleDocument = gql`
  mutation updateProjectAccessTeamWideAccessRole($inputs: UpdateTeamWideAccessInputs!) {
    updateProjectAccessTeamWideAccessRole(inputs: $inputs) {
      success
      message
      code
      role {
        id
        label
        permissions
      }
      teamMemberAccess {
        id
        teamId
        projectId
        teamMember {
          id
          name
        }
        projectAccessRole {
          id
          label
          permissions
        }
      }
    }
  }
`;
export type UpdateProjectAccessTeamWideAccessRoleMutationFn = Apollo.MutationFunction<
  UpdateProjectAccessTeamWideAccessRoleMutation,
  UpdateProjectAccessTeamWideAccessRoleMutationVariables
>;

/**
 * __useUpdateProjectAccessTeamWideAccessRoleMutation__
 *
 * To run a mutation, you first call `useUpdateProjectAccessTeamWideAccessRoleMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateProjectAccessTeamWideAccessRoleMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateProjectAccessTeamWideAccessRoleMutation, { data, loading, error }] = useUpdateProjectAccessTeamWideAccessRoleMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useUpdateProjectAccessTeamWideAccessRoleMutation(
  baseOptions?: Apollo.MutationHookOptions<
    UpdateProjectAccessTeamWideAccessRoleMutation,
    UpdateProjectAccessTeamWideAccessRoleMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    UpdateProjectAccessTeamWideAccessRoleMutation,
    UpdateProjectAccessTeamWideAccessRoleMutationVariables
  >(UpdateProjectAccessTeamWideAccessRoleDocument, options);
}
export type UpdateProjectAccessTeamWideAccessRoleMutationHookResult = ReturnType<
  typeof useUpdateProjectAccessTeamWideAccessRoleMutation
>;
export type UpdateProjectAccessTeamWideAccessRoleMutationResult =
  Apollo.MutationResult<UpdateProjectAccessTeamWideAccessRoleMutation>;
export type UpdateProjectAccessTeamWideAccessRoleMutationOptions =
  Apollo.BaseMutationOptions<
    UpdateProjectAccessTeamWideAccessRoleMutation,
    UpdateProjectAccessTeamWideAccessRoleMutationVariables
  >;
export const UpdateProjectAccessTeamMemberAccessRoleDocument = gql`
  mutation updateProjectAccessTeamMemberAccessRole(
    $inputs: UpdateTeamMemberAccessInputs!
  ) {
    updateProjectAccessTeamMemberAccessRole(inputs: $inputs) {
      success
      message
      code
      teamMemberId
      role {
        id
        label
        permissions
      }
    }
  }
`;
export type UpdateProjectAccessTeamMemberAccessRoleMutationFn = Apollo.MutationFunction<
  UpdateProjectAccessTeamMemberAccessRoleMutation,
  UpdateProjectAccessTeamMemberAccessRoleMutationVariables
>;

/**
 * __useUpdateProjectAccessTeamMemberAccessRoleMutation__
 *
 * To run a mutation, you first call `useUpdateProjectAccessTeamMemberAccessRoleMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateProjectAccessTeamMemberAccessRoleMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateProjectAccessTeamMemberAccessRoleMutation, { data, loading, error }] = useUpdateProjectAccessTeamMemberAccessRoleMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useUpdateProjectAccessTeamMemberAccessRoleMutation(
  baseOptions?: Apollo.MutationHookOptions<
    UpdateProjectAccessTeamMemberAccessRoleMutation,
    UpdateProjectAccessTeamMemberAccessRoleMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    UpdateProjectAccessTeamMemberAccessRoleMutation,
    UpdateProjectAccessTeamMemberAccessRoleMutationVariables
  >(UpdateProjectAccessTeamMemberAccessRoleDocument, options);
}
export type UpdateProjectAccessTeamMemberAccessRoleMutationHookResult = ReturnType<
  typeof useUpdateProjectAccessTeamMemberAccessRoleMutation
>;
export type UpdateProjectAccessTeamMemberAccessRoleMutationResult =
  Apollo.MutationResult<UpdateProjectAccessTeamMemberAccessRoleMutation>;
export type UpdateProjectAccessTeamMemberAccessRoleMutationOptions =
  Apollo.BaseMutationOptions<
    UpdateProjectAccessTeamMemberAccessRoleMutation,
    UpdateProjectAccessTeamMemberAccessRoleMutationVariables
  >;
export const UpdateBulkTeamMemberAccessRoleDocument = gql`
  mutation updateBulkTeamMemberAccessRole($inputs: UpdateBulkTeamMemberAccessInputs!) {
    updateProjectAccessBulkTeamMemberAccessRole(inputs: $inputs) {
      success
      message
      code
      teamMemberIds
      role {
        id
        label
        permissions
      }
    }
  }
`;
export type UpdateBulkTeamMemberAccessRoleMutationFn = Apollo.MutationFunction<
  UpdateBulkTeamMemberAccessRoleMutation,
  UpdateBulkTeamMemberAccessRoleMutationVariables
>;

/**
 * __useUpdateBulkTeamMemberAccessRoleMutation__
 *
 * To run a mutation, you first call `useUpdateBulkTeamMemberAccessRoleMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateBulkTeamMemberAccessRoleMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateBulkTeamMemberAccessRoleMutation, { data, loading, error }] = useUpdateBulkTeamMemberAccessRoleMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useUpdateBulkTeamMemberAccessRoleMutation(
  baseOptions?: Apollo.MutationHookOptions<
    UpdateBulkTeamMemberAccessRoleMutation,
    UpdateBulkTeamMemberAccessRoleMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    UpdateBulkTeamMemberAccessRoleMutation,
    UpdateBulkTeamMemberAccessRoleMutationVariables
  >(UpdateBulkTeamMemberAccessRoleDocument, options);
}
export type UpdateBulkTeamMemberAccessRoleMutationHookResult = ReturnType<
  typeof useUpdateBulkTeamMemberAccessRoleMutation
>;
export type UpdateBulkTeamMemberAccessRoleMutationResult =
  Apollo.MutationResult<UpdateBulkTeamMemberAccessRoleMutation>;
export type UpdateBulkTeamMemberAccessRoleMutationOptions = Apollo.BaseMutationOptions<
  UpdateBulkTeamMemberAccessRoleMutation,
  UpdateBulkTeamMemberAccessRoleMutationVariables
>;
export const ProjectAccessEntityChangedDocument = gql`
  subscription ProjectAccessEntityChanged(
    $inputs: ProjectEntityChangedSubscriptionInputs!
  ) {
    projectAccessEntityChanged(inputs: $inputs) {
      projectId
      changeType
      eventTime
      projectAccessEntity {
        ...SubscriptionProjectAccessFragment
      }
    }
  }
  ${SubscriptionProjectAccessFragmentDoc}
`;

/**
 * __useProjectAccessEntityChangedSubscription__
 *
 * To run a query within a React component, call `useProjectAccessEntityChangedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useProjectAccessEntityChangedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useProjectAccessEntityChangedSubscription({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useProjectAccessEntityChangedSubscription(
  baseOptions: Apollo.SubscriptionHookOptions<
    ProjectAccessEntityChangedSubscription,
    ProjectAccessEntityChangedSubscriptionVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useSubscription<
    ProjectAccessEntityChangedSubscription,
    ProjectAccessEntityChangedSubscriptionVariables
  >(ProjectAccessEntityChangedDocument, options);
}
export type ProjectAccessEntityChangedSubscriptionHookResult = ReturnType<
  typeof useProjectAccessEntityChangedSubscription
>;
export type ProjectAccessEntityChangedSubscriptionResult =
  Apollo.SubscriptionResult<ProjectAccessEntityChangedSubscription>;
